import { AccessorKeyColumnDef, createColumnHelper, PaginationState } from '@tanstack/react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Table from './Table';
import useTableState from '../useTableState';
import useApi from '../../../hooks/use-api';
import { getLogbookActions, getLogbookData } from '../../../constants/routes';
import { useUser } from '../../../hooks/use-user';
import { Button } from 'react-bootstrap';

export type LogbookDataEntry = {
	id: number;
	title: string;
	username: string;
	date: string;
};

const columnHelper = createColumnHelper<LogbookDataEntry>();
const tableName = 'logbook-table';

const LogbookOverview = () => {
	const { t } = useTranslation();
	const { activeCompany } = useUser();
	const { setPageIndex, setPageSize, pageIndex, pageSize } = useTableState(tableName);

	const [searchValue, setSearchValue] = useState<string | null>(null);
	const [action, setAction] = useState<string | undefined>(undefined);

	const [pagination, setPagination] = React.useState<PaginationState>({
		pageIndex,
		pageSize,
	});

	useEffect(() => {
		setPageIndex(pagination.pageIndex);
		setPageSize(pagination.pageSize);
	}, [pagination]);

	const [{ data }, fetchLogbook] = useApi.get(
		getLogbookData(),
		{
			company: activeCompany.id,
			pagination: true,
			itemsPerPage: pagination.pageSize,
			page: pagination.pageIndex + 1,
		},
		{ accept: 'application/ld+json' },
	);
	const { totalItems: totalItems, member: results } = data || {};

	const [{ data: actionData }] = useApi.get(getLogbookActions());
	const logBookActionData = actionData as { action: string; translation: string }[];

	const updateLogbookData = () => {
		fetchLogbook({
			company: activeCompany.id,
			pagination: true,
			itemsPerPage: pageSize,
			page: pageIndex + 1, // Table pages are 0-indexed, where api platform pages are 1-indexed
			search: searchValue,
			...(action ? { action } : {}),
		});
	};

	const columns = useMemo(
		() =>
			[
				columnHelper.accessor('title', {
					header: t('logbook.title'),
				}),
				columnHelper.accessor('username', {
					header: t('logbook.user'),
				}),
				columnHelper.accessor('date', {
					header: t('logbook.date'),
				}),
			] as Array<AccessorKeyColumnDef<LogbookDataEntry, unknown>>,
		[],
	);

	return (
		<div className="col-12 col-xl-10 mx-auto mt-3">
			<div className="d-flex justify-content-start" style={{ width: '100%' }}>
				<div className="mx-3">
					<label>
						{t('table.search')}:
						<input
							className="form-control form-control-sm d-inline-block"
							value={searchValue || ''}
							onChange={(e) => setSearchValue(e.target.value)}
						/>
					</label>
				</div>
				<div className="mx-3">
					<label>
						{t('logbook.action')}
						<select
							className="form-select form-select-sm d-inline-block"
							value={action}
							onChange={(e) => {
								setAction(e.target.value);
							}}>
							<option value="">Alle</option>
							{logBookActionData &&
								logBookActionData.map(({ action: key, translation }) => (
									<option value={key} key={key}>
										{translation}
									</option>
								))}
						</select>
					</label>
				</div>
				<Button variant="primary" className="align-self-center ms-auto" onClick={updateLogbookData}>
					{t('table.search')}
				</Button>
			</div>
			<Table
				columns={columns}
				data={results || []}
				pagination={pagination}
				setPagination={setPagination}
				totalItems={totalItems}
			/>
		</div>
	);
};

export default LogbookOverview;
